import React, { useEffect } from 'react'
import { Box } from '@mui/material'
import { getTargets, SKIP_LINKS_SELECTORS, SKIP_LINKS_TITLES } from './helpers'
import SkipLink from './components/SkipLink'
import { SkipLinkType } from './types'

interface Props {
  outterTargets?: [string, HTMLElement][]
}

const SKIP_LINKS_NAVIGATION = 'SKIP_LINKS_NAVIGATION'

const SkipLinks: React.FC<Props> = ({ outterTargets }) => {
  const skipLinkIds = Object.entries(SKIP_LINKS_SELECTORS)
  const targets = outterTargets ?? getTargets(skipLinkIds)

  // Always show skip links, even if target not found yet (will be found when clicked)
  const focusableTargets = targets.filter(([targetType]) => !!targetType) as [
    SkipLinkType,
    HTMLElement | null,
  ][]

  const handleSkipLinks = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      const activeElement = document.activeElement as HTMLElement | null
      // check if element is inside the container #SKIP_LINKS_NAVIGATION, if not, then do not blur
      const isSkipLinkElement = activeElement?.closest(`#${SKIP_LINKS_NAVIGATION}`)

      // only blur focus when Escape is pressed while a skip link is focused.
      if (isSkipLinkElement) {
        activeElement.blur()
      }
    }
  }

  useEffect(() => {
    window.addEventListener('keyup', handleSkipLinks)

    return () => {
      window.removeEventListener('keyup', handleSkipLinks)
    }
  }, [])

  return !!focusableTargets.length ? (
    <Box id={SKIP_LINKS_NAVIGATION} component='nav' data-automation-id='skip-links-navigation'>
      <Box m={0} p={0} component='ul'>
        {focusableTargets.map(([targetType]) => {
          const handleClick = () => {
            // Query for the target element when clicked, in case it wasn't mounted initially
            const target = document.querySelector(SKIP_LINKS_SELECTORS[targetType].value) as HTMLElement
            target?.focus()
          }

          return (
            <SkipLink
              key={targetType}
              onClick={handleClick}
              onKeyUp={e => e.key === 'Enter' && handleClick()}
            >
              {SKIP_LINKS_TITLES[targetType]}
            </SkipLink>
          )
        })}
      </Box>
    </Box>
  ) : null
}

export default SkipLinks
